import { FC, useState } from "react";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import DateSelectionModal from "shared/DateField/DateSelectionModal";
import { IoMdCloseCircleOutline } from "react-icons/io";
import { CheckCircleIcon, SparklesIcon } from "@heroicons/react/24/outline";

interface QueryActionsProps {
  queryState: string;
  isHost: boolean;
  receiverName: string;
  senderName: string;
  propertySlug: string;
  bookingRequestDetails: any;
  onInviteToBook: () => void;
  onDeclineQuery: () => void;
  onBookingRequest: () => void;
  onDateChange: (dates: any) => void;
}

const QueryActions: FC<QueryActionsProps> = ({
  queryState,
  isHost,
  receiverName,
  senderName,
  propertySlug,
  bookingRequestDetails,
  onInviteToBook,
  onDeclineQuery,
  onBookingRequest,
  onDateChange,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleDateSubmit = (dates: any) => {
    onDateChange(dates);
    setIsModalOpen(false);
  };

  // Rejected state - show for both host and guest
  if (queryState === "rejected") {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
        <p className="text-lg gap-2 text-red-600 dark:text-red-400 font-bold flex items-center">
          <IoMdCloseCircleOutline className="w-6 h-6" />
          <span>Request Declined</span>
        </p>
        <p className="text-sm text-red-600 dark:text-red-400 mt-2">
          This booking query has been declined and is no longer active.
        </p>
      </div>
    );
  }

  // Host view
  if (isHost) {
    return (
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/50 dark:to-indigo-950/50 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
        {/* Show courtesy message if invitation was sent */}
        {queryState === "booking_invitation" && (
          <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
            <div className="flex items-center gap-2">
              <CheckCircleIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
              <p className="text-sm font-medium text-green-800 dark:text-green-200">
                Invitation sent to {senderName}. They have 48 hours to respond.
              </p>
            </div>
          </div>
        )}

        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            {queryState === "booking_invitation" ? "Invitation Sent" : "Invite to Book"}
          </h3>
          <p className="text-sm text-gray-700 dark:text-gray-300">
            {queryState === "booking_invitation" 
              ? `You have invited ${senderName} to book your place. They will be able to book your place immediately within the next 48 hours.`
              : `Invite ${senderName} to book your place. They will be able to book your place immediately within the next 48 hours after you invite them to book.`
            }
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          {queryState === "booking_invitation" ? (
            <ButtonPrimary
              className="bg-gray-500 text-white px-6 py-3 rounded-lg cursor-not-allowed"
              disabled={true}
            >
              Invitation Sent
            </ButtonPrimary>
          ) : (
            <ButtonPrimary
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors"
              onClick={onInviteToBook}
            >
              Invite to Book
            </ButtonPrimary>
          )}
          <ButtonPrimary
            className="bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-lg transition-colors"
            onClick={onDeclineQuery}
          >
            Decline
          </ButtonPrimary>
        </div>
      </div>
    );
  }

  // Guest view - ALWAYS show actions regardless of invitation status
  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/50 dark:to-indigo-950/50 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
      {/* Show courtesy message if host sent invitation */}
      {queryState === "booking_invitation" && (
        <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg">
          <div className="flex items-center gap-2">
            <SparklesIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
              {receiverName} has invited you to book this property!
            </p>
          </div>
        </div>
      )}

      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
          Move Forward with Booking
        </h3>
        <p className="text-sm text-gray-700 dark:text-gray-300">
          Request a booking from{" "}
          <span className="font-semibold">{receiverName}</span> to secure
          this place. You will not be charged until your request has been
          accepted.
        </p>
      </div>
      
      {/* Guest actions - ALWAYS AVAILABLE */}
      <div className="flex flex-wrap gap-3">
        <ButtonPrimary
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors"
          onClick={onBookingRequest}
        >
          Send Booking Request
        </ButtonPrimary>
        <ButtonPrimary
          className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-lg transition-colors"
          onClick={() => setIsModalOpen(true)}
        >
          Change Requested Dates
        </ButtonPrimary>
        <ButtonPrimary
          href={`/listing-stay-detail/${propertySlug}`}
          className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-lg transition-colors"
        >
          Visit Property
        </ButtonPrimary>
      </div>

      <DateSelectionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleDateSubmit}
        bookingRequestDetails={bookingRequestDetails}
      />
    </div>
  );
};

export default QueryActions;